import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert, Text, api, getQueryProps, isDefined, parseError, useToast } from 'common';

import { CreateScheduleDialog } from './CreateScheduleDialog';
import { FLOW_DEFINITION_STATE_TO_SCHEDULED_TRIGGERS_ENABLED } from './utils';
import { useFlowDefinitionContext } from '../../../../context';

interface ScheduledTriggersProps {
  flowId: string;
  flowDefinitionId: string;
}

export function ScheduledTriggers(props: ScheduledTriggersProps) {
  const { flowId, flowDefinitionId } = props;
  const { flowDefinition } = useFlowDefinitionContext();
  const toast = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {
    data: triggers = [],
    refetch,
    isSuccess,
    isLoading,
    error,
  } = useQuery(getQueryProps(api.client.FlowTriggerSchedules, 'findAll', { flowDefinitionId }));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.client.FlowTriggerSchedules.delete(id),
    onSuccess: async () => {
      await refetch();
    },
    onError: (error) => {
      toast.showError(parseError(error, 'Failed to delete schedule'));
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => api.client.FlowTriggerSchedules.pause(id),
    onSuccess: async () => {
      await refetch();
    },
    onError: (error) => {
      toast.showError(parseError(error, 'Failed to pause schedule'));
    },
  });

  const unpauseMutation = useMutation({
    mutationFn: (id: string) => api.client.FlowTriggerSchedules.unpause(id),
    onSuccess: async () => {
      await refetch();
    },
    onError: (error) => {
      toast.showError(parseError(error, 'Failed to resume schedule'));
    },
  });

  if (isLoading) {
    return <Skeleton variant="rectangular" height={80} />;
  }

  if (!isSuccess) {
    return <Alert title="Unable to load scheduled triggers" variant="error" error={error} />;
  }

  const formatCronExpression = (trigger: api.FlowTriggerSchedules.ScheduledFlowTrigger): string => {
    const { scheduleSpec } = trigger;
    if (isDefined(scheduleSpec.cronExpressions) && scheduleSpec.cronExpressions.length > 0) {
      return scheduleSpec.cronExpressions.join(', ');
    }
    return 'Custom schedule';
  };

  return (
    <Stack spacing={2}>
      {triggers.length > 0 && (
        <>
          <Text variant="heading6">Scheduled Triggers</Text>
          {triggers.map((trigger) => {
            const { id, name, isActive, scheduleSpec } = trigger;
            return (
              <Card key={id} variant="outlined">
                <CardContent>
                  <Stack gap={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Text variant="body2">{name}</Text>
                        <Chip
                          size="small"
                          label={isActive ? 'Active' : 'Paused'}
                          color={isActive ? 'success' : 'default'}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Copy schedule ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              void navigator.clipboard.writeText(id);
                              toast.showInfo('Schedule ID copied to clipboard');
                            }}
                            aria-label="Copy schedule id"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isActive ? (
                          <Tooltip title="Pause schedule">
                            <IconButton
                              size="small"
                              onClick={() => pauseMutation.mutate(id)}
                              loading={pauseMutation.isPending}
                              aria-label="Pause schedule"
                            >
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Resume schedule">
                            <IconButton
                              size="small"
                              onClick={() => unpauseMutation.mutate(id)}
                              loading={unpauseMutation.isPending}
                              disabled={
                                FLOW_DEFINITION_STATE_TO_SCHEDULED_TRIGGERS_ENABLED[
                                  flowDefinition.state
                                ] === 'CannotScheduleTriggers'
                              }
                              aria-label="Resume schedule"
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete schedule">
                          <IconButton
                            size="small"
                            onClick={() => deleteMutation.mutate(id)}
                            loading={deleteMutation.isPending}
                            aria-label="Delete schedule"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Text variant="subtitle2" color="text.secondary">
                      CRON: {formatCronExpression(trigger)}
                    </Text>
                    {isDefined(scheduleSpec.timezone) && (
                      <Text variant="subtitle2" color="text.secondary">
                        Time Zone: {scheduleSpec.timezone}
                      </Text>
                    )}
                    <Text variant="subtitle2" color="text.secondary">
                      Available for{' '}
                      {trigger.allowUnpublishedFlowRuns
                        ? 'published and unpublished'
                        : 'only published'}{' '}
                      flows
                    </Text>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          <Divider />
        </>
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setIsCreateDialogOpen(true)}
        size="small"
        disabled={
          FLOW_DEFINITION_STATE_TO_SCHEDULED_TRIGGERS_ENABLED[flowDefinition.state] ===
          'CannotScheduleTriggers'
        }
      >
        Add Scheduled Trigger
      </Button>

      <CreateScheduleDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        flowId={flowId}
        flowDefinitionId={flowDefinitionId}
        organizationId={flowDefinition.organizationId}
        onScheduleCreated={async () => {
          setIsCreateDialogOpen(false);
          await refetch();
        }}
      />
    </Stack>
  );
}
